import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Search, TrendingUp, MousePointerClick, Eye, ArrowUpRight, ExternalLink, AlertCircle, Globe, Monitor, Smartphone, Tablet, RefreshCw, ArrowUp, ArrowDown, Minus, Download, History, BarChart3 } from 'lucide-react'
import {
  useGscStatus,
  useGscPerformance,
  useGscQueries,
  useGscLandingPages,
  useGscCountries,
  useGscDevices,
  useRefreshGscCache,
  type GscSiteMatch,
} from '../hooks/useGsc'
import {
  useGscSnapshotQueries,
  useGscSnapshotTimeseries,
  useTakeGscSnapshot,
  useGscBackfill,
  useEnableGscSnapshots,
  type GscSnapshotQuery,
} from '../hooks/useGscSnapshots'
import { rankColor } from '@/client/lib/colors'

interface SearchConsoleTabProps {
  siteId: string
  domain: string
  tier?: string
}

export function SearchConsoleTab({ siteId, domain, tier }: SearchConsoleTabProps) {
  const { data: status, isLoading: statusLoading, isError, error, refetch } = useGscStatus(siteId)

  if (statusLoading) {
    return (
      <div className="space-y-6">
        {/* Skeleton summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-4 pb-3 px-4">
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-7 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Skeleton chart */}
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-4 w-32 mb-4" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
        {/* Skeleton table */}
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-4 w-28 mb-4" />
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-destructive mb-4" />
          <h3 className="text-lg font-semibold">Failed to load GSC data</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            {(error as Error)?.message || 'Could not connect to Google Search Console.'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (!status?.connected || status.accounts.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Search className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">No GSC Account Connected</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            Connect a Google Search Console account to see search performance data for {domain}.
          </p>
          <Button asChild>
            <a href="/dashboard/integrations">Manage GSC Connections</a>
          </Button>
        </CardContent>
      </Card>
    )
  }

  return <GscDataView siteId={siteId} accounts={status.accounts} tier={tier} />
}

type SubTab = 'queries' | 'pages' | 'countries' | 'devices' | 'rankings'

function GscDataView({ siteId, accounts, tier }: { siteId: string; accounts: GscSiteMatch[]; tier?: string }) {
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [days, setDays] = useState(28)
  const [subTab, setSubTab] = useState<SubTab>('queries')

  const account = accounts[selectedIdx] ?? accounts[0]
  if (!account) return null
  const siteUrl = account.siteUrl

  const { data: perf, isLoading: perfLoading } = useGscPerformance(siteId, siteUrl, days)
  const { data: queries, isLoading: queriesLoading } = useGscQueries(siteId, siteUrl, days)
  const { data: landingPages, isLoading: pagesLoading } = useGscLandingPages(siteId, siteUrl, days)
  const { data: countries, isLoading: countriesLoading } = useGscCountries(siteId, siteUrl, days)
  const { data: devices, isLoading: devicesLoading } = useGscDevices(siteId, siteUrl, days)
  const refreshCache = useRefreshGscCache(siteId)

  return (
    <div className="space-y-6">
      {/* Account selector (if multiple) */}
      {accounts.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          {accounts.map((acc, i) => (
            <Button
              key={acc.siteUrl}
              variant={i === selectedIdx ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedIdx(i)}
            >
              {acc.email}
            </Button>
          ))}
        </div>
      )}

      {/* Connected account info */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <ExternalLink className="h-3.5 w-3.5" />
        <span>{account.siteUrl}</span>
        <Badge variant="outline" className="text-xs">{account.permissionLevel}</Badge>
        <span className="ml-auto flex items-center gap-2">
          <span className="text-xs">{account.email}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => refreshCache.mutate()}
            disabled={refreshCache.isPending}
            title="Refresh data from Google"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshCache.isPending ? 'animate-spin' : ''}`} />
          </Button>
        </span>
      </div>

      {/* Summary cards */}
      <div className={`grid grid-cols-2 sm:grid-cols-4 gap-4 transition-opacity ${perfLoading ? 'opacity-50 pointer-events-none' : ''}`}>
        <MetricCard
          icon={<MousePointerClick className="h-5 w-5" />}
          label="Total Clicks"
          value={perfLoading ? null : perf?.totals.clicks.toLocaleString() ?? '0'}
        />
        <MetricCard
          icon={<Eye className="h-5 w-5" />}
          label="Total Impressions"
          value={perfLoading ? null : perf?.totals.impressions.toLocaleString() ?? '0'}
        />
        <MetricCard
          icon={<TrendingUp className="h-5 w-5" />}
          label="Avg CTR"
          value={perfLoading ? null : perf?.totals.ctr != null ? `${(perf.totals.ctr * 100).toFixed(1)}%` : '—'}
        />
        <MetricCard
          icon={<ArrowUpRight className="h-5 w-5" />}
          label="Avg Position"
          value={perfLoading ? null : queries?.queries.length
            ? (queries.queries.reduce((s, q) => s + q.position * q.impressions, 0) / Math.max(queries.totals.impressions, 1)).toFixed(1)
            : '—'}
        />
      </div>

      {/* Date range pills */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Period:</span>
        {[7, 28, 90].map((d) => (
          <Button
            key={d}
            variant={days === d ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDays(d)}
          >
            {d} days
          </Button>
        ))}
      </div>

      {/* Daily trend chart */}
      <Card className={`transition-opacity ${perfLoading ? 'opacity-50 pointer-events-none' : ''}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Daily Performance</CardTitle>
        </CardHeader>
        <CardContent>
          {perfLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : perf && perf.daily.length > 0 ? (
            <DailyChart data={perf.daily} />
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No data for this period.</p>
          )}
        </CardContent>
      </Card>

      {/* Sub-tab selector */}
      <div className="flex items-center gap-1 border-b">
        {([
          { key: 'queries', label: 'Queries' },
          { key: 'pages', label: 'Landing Pages' },
          { key: 'countries', label: 'Countries' },
          { key: 'devices', label: 'Devices' },
          { key: 'rankings', label: 'Rankings' },
        ] as const).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setSubTab(tab.key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              subTab === tab.key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sub-tab content */}
      {subTab === 'queries' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top Search Queries</CardTitle>
          </CardHeader>
          <CardContent>
            {queriesLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : queries && queries.queries.length > 0 ? (
              <GscTable
                columns={['Query', 'Clicks', 'Impressions', 'CTR', 'Position']}
                rows={queries.queries.map((q) => ({
                  key: q.query,
                  cells: [
                    q.query,
                    q.clicks.toLocaleString(),
                    q.impressions.toLocaleString(),
                    `${(q.ctr * 100).toFixed(1)}%`,
                    q.position.toFixed(1),
                  ],
                }))}
              />
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No queries found.</p>
            )}
          </CardContent>
        </Card>
      )}

      {subTab === 'pages' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top Landing Pages</CardTitle>
          </CardHeader>
          <CardContent>
            {pagesLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : landingPages && landingPages.pages.length > 0 ? (
              <GscTable
                columns={['Page', 'Clicks', 'Impressions', 'CTR', 'Position']}
                rows={landingPages.pages.map((p) => ({
                  key: p.page,
                  cells: [
                    p.page,
                    p.clicks.toLocaleString(),
                    p.impressions.toLocaleString(),
                    `${(p.ctr * 100).toFixed(1)}%`,
                    p.position.toFixed(1),
                  ],
                }))}
                truncateFirst
              />
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No landing page data.</p>
            )}
          </CardContent>
        </Card>
      )}

      {subTab === 'countries' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Performance by Country</CardTitle>
          </CardHeader>
          <CardContent>
            {countriesLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : countries && countries.countries.length > 0 ? (
              <div className="space-y-4">
                <GscTable
                  columns={['Country', 'Clicks', 'Impressions', 'CTR', 'Position']}
                  rows={countries.countries.map((co) => ({
                    key: co.country,
                    cells: [
                      co.country,
                      co.clicks.toLocaleString(),
                      co.impressions.toLocaleString(),
                      `${(co.ctr * 100).toFixed(1)}%`,
                      co.position.toFixed(1),
                    ],
                    icon: <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />,
                  }))}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No country data.</p>
            )}
          </CardContent>
        </Card>
      )}

      {subTab === 'rankings' && (
        <RankingsView siteId={siteId} tier={tier} />
      )}

      {subTab === 'devices' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Performance by Device</CardTitle>
          </CardHeader>
          <CardContent>
            {devicesLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : devices && devices.devices.length > 0 ? (
              <div className="space-y-4">
                {/* Device visual breakdown */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {devices.devices.map((d) => {
                    const icon = d.device === 'MOBILE' ? <Smartphone className="h-5 w-5" />
                      : d.device === 'TABLET' ? <Tablet className="h-5 w-5" />
                      : <Monitor className="h-5 w-5" />
                    return (
                      <Card key={d.device}>
                        <CardContent className="pt-4 pb-3 px-4">
                          <div className="flex items-center gap-2 text-muted-foreground mb-2">
                            {icon}
                            <span className="text-sm font-medium capitalize">{d.device.toLowerCase()}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <p className="text-muted-foreground">Clicks</p>
                              <p className="font-bold text-sm">{d.clicks.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Impressions</p>
                              <p className="font-bold text-sm">{d.impressions.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">CTR</p>
                              <p className="font-bold text-sm">{(d.ctr * 100).toFixed(1)}%</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Avg Pos</p>
                              <p className="font-bold text-sm">{d.position.toFixed(1)}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No device data.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ── Rankings View ──────────────────────────────────────────────────

function PositionBadge({ position }: { position: number }) {
  return <span className={`font-mono font-bold ${rankColor(position)}`}>{position.toFixed(1)}</span>
}

function ChangeIndicator({ value, inverted }: { value: number | null; inverted?: boolean }) {
  if (value == null) return <span className="text-muted-foreground text-xs">—</span>
  // For position: negative change = improvement (moved up), so invert
  const isGood = inverted ? value < 0 : value > 0
  const isBad = inverted ? value > 0 : value < 0
  const Icon = isGood ? ArrowUp : isBad ? ArrowDown : Minus
  const colour = isGood ? 'text-green-600 dark:text-green-400'
    : isBad ? 'text-red-600 dark:text-red-400'
    : 'text-muted-foreground'
  const display = inverted ? Math.abs(value).toFixed(1) : Math.abs(value).toLocaleString()
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs ${colour}`}>
      <Icon className="h-3 w-3" />
      {display}
    </span>
  )
}

function RankingsView({ siteId, tier }: { siteId: string; tier?: string }) {
  const { data, isLoading } = useGscSnapshotQueries(siteId)
  const takeSnapshot = useTakeGscSnapshot(siteId)
  const backfill = useGscBackfill(siteId)
  const enableSnapshots = useEnableGscSnapshots(siteId)
  const [expandedQuery, setExpandedQuery] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<'impressions' | 'clicks' | 'position'>('impressions')

  if (isLoading) return <Skeleton className="h-48 w-full" />

  const queries = data?.queries ?? []

  if (!queries.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <History className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground mb-4">
            No GSC ranking snapshots yet. Snapshots capture weekly query performance data from Google Search Console.
          </p>
          {tier === 'managed' && (
            <div className="flex gap-2 justify-center">
              <Button
                size="sm"
                onClick={() => enableSnapshots.mutate()}
                disabled={enableSnapshots.isPending}
              >
                Enable Weekly Snapshots
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => takeSnapshot.mutate()}
                disabled={takeSnapshot.isPending}
              >
                {takeSnapshot.isPending ? 'Taking snapshot...' : 'Take Snapshot Now'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  const sorted = [...queries].sort((a, b) => {
    if (sortBy === 'clicks') return b.latestClicks - a.latestClicks
    if (sortBy === 'position') return a.latestPosition - b.latestPosition
    return b.latestImpressions - a.latestImpressions
  })

  return (
    <div className="space-y-4">
      {/* Actions bar */}
      {tier === 'managed' && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() => takeSnapshot.mutate()}
            disabled={takeSnapshot.isPending}
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${takeSnapshot.isPending ? 'animate-spin' : ''}`} />
            {takeSnapshot.isPending ? 'Snapshotting...' : 'Snapshot Now'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => backfill.mutate()}
            disabled={backfill.isPending}
          >
            <History className={`h-3.5 w-3.5 mr-1.5`} />
            {backfill.isPending ? 'Backfilling...' : 'Backfill 16 Months'}
          </Button>
          <a
            href={`/api/sites/${siteId}/gsc/snapshots/export?format=csv`}
            download
            className="inline-flex"
          >
            <Button size="sm" variant="outline">
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Export CSV
            </Button>
          </a>
          {data?.snapshotDate && (
            <span className="ml-auto text-xs text-muted-foreground">
              Latest: {data.snapshotDate}
            </span>
          )}
        </div>
      )}

      {/* Summary */}
      {takeSnapshot.isSuccess && (
        <div className="text-sm text-green-600 dark:text-green-400">
          Snapshot captured {takeSnapshot.data?.count ?? 0} queries.
        </div>
      )}
      {backfill.isSuccess && (
        <div className="text-sm text-green-600 dark:text-green-400">
          Backfill complete: {backfill.data?.count ?? 0} data points stored.
        </div>
      )}

      {/* Rankings table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              GSC Query Rankings
              <Badge variant="outline" className="text-xs">{queries.length} queries</Badge>
            </CardTitle>
            <div className="flex gap-1">
              {(['impressions', 'clicks', 'position'] as const).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={sortBy === s ? 'default' : 'outline'}
                  className="h-7 px-2 text-xs capitalize"
                  onClick={() => setSortBy(s)}
                >
                  {s}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-4">Query</th>
                  <th className="pb-2 pr-4 text-right">Position</th>
                  <th className="pb-2 pr-4 text-right">Change</th>
                  <th className="pb-2 pr-4 text-right">Clicks</th>
                  <th className="pb-2 pr-4 text-right">Impr.</th>
                  <th className="pb-2 pr-4 text-right">CTR</th>
                  {sorted.some(q => q.dfsRank != null) && (
                    <th className="pb-2 pr-4 text-right">DFS Rank</th>
                  )}
                  <th className="pb-2 text-right">Trend</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((q) => (
                  <RankingRow
                    key={q.query}
                    q={q}
                    siteId={siteId}
                    expanded={expandedQuery === q.query}
                    onToggle={() => setExpandedQuery(expandedQuery === q.query ? null : q.query)}
                    showDfs={sorted.some(r => r.dfsRank != null)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function RankingRow({ q, siteId, expanded, onToggle, showDfs }: {
  q: GscSnapshotQuery
  siteId: string
  expanded: boolean
  onToggle: () => void
  showDfs: boolean
}) {
  return (
    <>
      <tr
        className="border-b last:border-0 hover:bg-accent/50 cursor-pointer"
        onClick={onToggle}
      >
        <td className="py-2 pr-4 font-medium max-w-[250px] truncate" title={q.query}>
          {q.query}
        </td>
        <td className="py-2 pr-4 text-right">
          <PositionBadge position={q.latestPosition} />
        </td>
        <td className="py-2 pr-4 text-right">
          <ChangeIndicator value={q.positionChange} inverted />
        </td>
        <td className="py-2 pr-4 text-right">{q.latestClicks.toLocaleString()}</td>
        <td className="py-2 pr-4 text-right">{q.latestImpressions.toLocaleString()}</td>
        <td className="py-2 pr-4 text-right">{(q.latestCtr * 100).toFixed(1)}%</td>
        {showDfs && (
          <td className="py-2 pr-4 text-right">
            {q.dfsRank != null ? (
              <Badge variant="outline" className="text-xs font-mono">{q.dfsRank}</Badge>
            ) : (
              <span className="text-muted-foreground text-xs">—</span>
            )}
          </td>
        )}
        <td className="py-2 text-right">
          {q.snapshotCount > 1 && (
            <Badge variant="outline" className="text-xs">{q.snapshotCount}w</Badge>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={showDfs ? 8 : 7} className="p-4 bg-accent/30">
            <QueryTimeseriesChart siteId={siteId} query={q.query} />
          </td>
        </tr>
      )}
    </>
  )
}

function QueryTimeseriesChart({ siteId, query }: { siteId: string; query: string }) {
  const { data, isLoading } = useGscSnapshotTimeseries(siteId, query)

  if (isLoading) return <Skeleton className="h-32 w-full" />
  const series = data?.timeseries ?? []
  if (series.length < 2) return <p className="text-sm text-muted-foreground">Not enough data points for a chart.</p>

  const maxClicks = Math.max(...series.map(d => d.clicks), 1)
  const maxPos = Math.max(...series.map(d => d.position), 1)

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">"{query}" — {series.length} weeks of data</p>
      <div className="grid grid-cols-2 gap-4">
        {/* Position chart */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">Position (lower is better)</p>
          <div className="flex items-end gap-0.5 h-20">
            {series.map((d) => (
              <div
                key={d.snapshot_date}
                className="flex-1 bg-blue-500/60 rounded-t-sm"
                style={{ height: `${(d.position / maxPos) * 100}%` }}
                title={`${d.snapshot_date}: pos ${d.position.toFixed(1)}`}
              />
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
            <span>{series[0]?.snapshot_date.slice(5)}</span>
            <span>{series[series.length - 1]?.snapshot_date.slice(5)}</span>
          </div>
        </div>
        {/* Clicks chart */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">Clicks</p>
          <div className="flex items-end gap-0.5 h-20">
            {series.map((d) => (
              <div
                key={d.snapshot_date}
                className="flex-1 bg-green-500/60 rounded-t-sm"
                style={{ height: `${(d.clicks / maxClicks) * 100}%` }}
                title={`${d.snapshot_date}: ${d.clicks} clicks`}
              />
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
            <span>{series[0]?.snapshot_date.slice(5)}</span>
            <span>{series[series.length - 1]?.snapshot_date.slice(5)}</span>
          </div>
        </div>
      </div>
      {/* Latest metrics summary */}
      <div className="flex gap-4 text-xs text-muted-foreground">
        <span>Latest: pos {series[series.length - 1]?.position.toFixed(1)}</span>
        <span>{series[series.length - 1]?.clicks} clicks</span>
        <span>{series[series.length - 1]?.impressions.toLocaleString()} impressions</span>
        <span>{((series[series.length - 1]?.ctr ?? 0) * 100).toFixed(1)}% CTR</span>
      </div>
    </div>
  )
}

/** Reusable GSC data table */
function GscTable({ columns, rows, truncateFirst }: {
  columns: string[]
  rows: Array<{ key: string; cells: string[]; icon?: React.ReactNode }>
  truncateFirst?: boolean
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            {columns.map((col, i) => (
              <th key={col} className={`pb-2 ${i < columns.length - 1 ? 'pr-4' : ''} ${i > 0 ? 'text-right' : ''}`}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b last:border-0 hover:bg-accent/50">
              {row.cells.map((cell, i) => (
                <td
                  key={i}
                  className={`py-2 ${i < row.cells.length - 1 ? 'pr-4' : ''} ${i > 0 ? 'text-right' : 'font-medium'} ${
                    i === 0 && truncateFirst ? 'max-w-[300px] truncate' : ''
                  }`}
                  title={i === 0 && truncateFirst ? cell : undefined}
                >
                  {i === 0 && row.icon ? (
                    <span className="flex items-center gap-1.5">{row.icon}{cell}</span>
                  ) : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function MetricCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | null }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center gap-2 text-muted-foreground mb-1">
          {icon}
          <span className="text-xs">{label}</span>
        </div>
        {value === null ? (
          <Skeleton className="h-7 w-16" />
        ) : (
          <div className="text-2xl font-bold">{value}</div>
        )}
      </CardContent>
    </Card>
  )
}

function DailyChart({ data }: { data: Array<{ date: string; clicks: number; impressions: number }> }) {
  const maxClicks = Math.max(...data.map((d) => d.clicks), 1)
  const maxImpressions = Math.max(...data.map((d) => d.impressions), 1)

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-primary" /> Clicks
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-primary/20" /> Impressions
        </span>
      </div>
      <div className="flex items-end gap-0.5 h-24" role="img" aria-label="Daily clicks and impressions chart">
        {data.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-0" title={`${d.date}: ${d.clicks} clicks, ${d.impressions} impressions`}>
            <div className="w-full relative" style={{ height: '96px' }}>
              {/* Impressions bar (background) */}
              <div
                className="absolute bottom-0 w-full bg-primary/15 rounded-t-sm"
                style={{ height: `${(d.impressions / maxImpressions) * 100}%` }}
              />
              {/* Clicks bar (foreground) */}
              <div
                className="absolute bottom-0 w-full bg-primary rounded-t-sm"
                style={{ height: `${(d.clicks / maxClicks) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{data[0]?.date.slice(5)}</span>
        <span>{data[data.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  )
}
